import { Box, Typography, CircularProgress } from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { getCategories } from '../Apis/CategoriesApis'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'

const ServicesSection = () => {
    const navigate = useNavigate()
    const [services, setServices] = React.useState([])
    const [loading, setLoading] = React.useState(true)
    const swiperRef = React.useRef(null)

    React.useEffect(() => {
        setLoading(true)
        getCategories().then((data) => {
            if (data && data.status) {
                const fetchedServices = data.categories || data.data;
                if (Array.isArray(fetchedServices)) {
                    setServices(fetchedServices)
                }
            }
        }).finally(() => {
            setLoading(false)
        })
    }, [])

    const handleServiceClick = (service) => {
        navigate(`/service/${service.id}`, {
            state: {
                categoryId: service.id,
                plans: service.plans,
                options: service.CategoryOptions,
                isOption: service.isOption,
                image: service.image,
                BoxText: service.BoxText,
                name: service.name
            }
        })
    }
    return (    
        <Box id="services" sx={{
            width: '100%',
            py: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 2, md: 4 }
        }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', minHeight: 200 }}>
                    <CircularProgress sx={{ color: '#ff6a00' }} />
                </Box>
            ) : (
                <>
                    {/* Swiper for large screens (md and up) */}
                    <Box
                        sx={{ 
                            width: '100%',
                            display: { xs: 'none', md: 'block' }
                        }}
                        onMouseEnter={() => {
                            if (swiperRef.current && swiperRef.current.swiper) {
                                swiperRef.current.swiper.autoplay.stop();
                            }
                        }}
                        onMouseLeave={() => {
                            if (swiperRef.current && swiperRef.current.swiper) {
                                swiperRef.current.swiper.autoplay.start();
                            }
                        }}
                    >
                        <Swiper
                            ref={swiperRef}
                            modules={[Autoplay]}
                            spaceBetween={15}
                            slidesPerView={2}
                            speed={2000}
                            autoplay={{
                                delay: 0,
                                disableOnInteraction: false,
                            }}
                            className="swiper-wrapper-linear"
                            grabCursor={true}
                            breakpoints={{
                                640: {
                                    slidesPerView: 3,
                                    spaceBetween: 20,
                                },
                                768: {
                                    slidesPerView: 4,
                                    spaceBetween: 20,
                                },
                                1024: {
                                    slidesPerView: 5,
                                    spaceBetween: 25,
                                },
                                1200: {
                                    slidesPerView: 6,
                                    spaceBetween: 30,
                                },
                            }}
                            loop={services.length >= 2}
                            style={{ padding: '15px 10px 40px 10px', margin: '0 -10px' }}
                        >
                            {services.map((service) => (
                                <SwiperSlide key={service.id || service.name} style={{ display: 'flex', justifyContent: 'center', height: 'auto' }}>
                                    <Box
                                        sx={{
                                            background: '#fff',
                                            borderRadius: 3,
                                            boxShadow: '0 2px 10px 0 rgba(0,0,0,0.06)',
                                            p: 2,
                                            width: '100%',
                                            height: '100%',
                                            minHeight: 120,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                                                transform: 'translateY(-3px)',
                                            },
                                        }}
                                        onClick={() => handleServiceClick(service)}
                                    >
                                        <Box
                                            component="img"
                                            src={service.logo}
                                            alt={service.name}
                                            sx={{
                                                width: 55,
                                                height: 55,
                                                mb: 1.5,
                                                objectFit: 'contain',
                                            }}
                                        />
                                        <Typography
                                            variant="subtitle2"
                                            color="black"
                                            align="center"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '0.9rem',
                                                lineHeight: 1.2,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {service.name}
                                        </Typography>
                                    </Box>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </Box>

                    {/* Flex layout for mobile (xs and sm) */}
                    <Box
                        sx={{
                            width: '100%',
                            display: { xs: 'flex', md: 'none' },
                            flexWrap: 'wrap',
                            gap: 2,
                            justifyContent: 'center',
                        }}
                    >
                        {services.map((service) => (
                            <Box
                                key={service.id || service.name}
                                sx={{
                                    flex: '1 1 calc(50% - 8px)',
                                    minWidth: 'calc(50% - 8px)',
                                    background: '#fff',
                                    borderRadius: 3,
                                    boxShadow: '0 2px 10px 0 rgba(0,0,0,0.06)',
                                    p: 2,
                                    minHeight: 120,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                                        transform: 'translateY(-3px)',
                                    },
                                }}
                                onClick={() => handleServiceClick(service)}
                            >
                                <Box
                                    component="img"
                                    src={service.logo}
                                    alt={service.name}
                                    sx={{
                                        width: 55,
                                        height: 55,
                                        mb: 1.5,
                                        objectFit: 'contain',
                                    }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    color="black"
                                    align="center"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        lineHeight: 1.2,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {service.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    )
}

export default ServicesSection
